import React, {useMemo} from "react";
import {Table} from "../components/table/Table";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';

import {Alert, Button, IconButton, Stack, Tooltip, Rating} from "@mui/material";
import {Delete} from "../components/common/Delete";
import {useNavigate, useParams} from "react-router";
import {DefaultError} from "@tanstack/query-core";
import {enqueueSnackbar} from "notistack";
import {TextInput} from "../components/form/TextInput";
import {Highlighter} from "../components/common/Highlighter";
import {sanitizeFilterObject} from "../service/http.service";
import {TableHeaderProps} from "../components/table/TableTypes";
import {deleteUser, loadUsers, UsersFilter} from "../service/user.service";
import {User} from "../types/User";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import {UserEditForm} from "../components/user/UserEditForm";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

export const UsersPage: React.FC = () => {
    const {id: idToEdit} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient()

    const deleteMutation = useMutation<void, DefaultError, string>({
        mutationFn: (id) => deleteUser(id).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["users"]}).catch(e => {
                enqueueSnackbar({message: "Failed to clean cache after delete user: " + e.message, variant: 'error'})
            });
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to delete user: " + error.message, variant: 'error'})
        }
    });

    const [filter, setFilter] = React.useState<UsersFilter>({
        page: 0,
        size: 25,
        sortKey: 'createdAt',
        sortOrder: 'desc',
        username: '',
        phone: '',
        userType: '',
        login: '',
        withPremium: false,
    });

    const {data, isFetching, error} = useQuery({
        queryKey: ['users', filter],
        refetchOnWindowFocus: false,
        queryFn: () => {
            return loadUsers(userFilter(filter)).then(response => response.data)
        },
        placeholderData: keepPreviousData
    });


    const headers: TableHeaderProps<User>[] = useMemo(() => {
        return [
            {
                key: "username",
                title: "User Name",
                render: (row) => <Highlighter text={row.username} search={filter.username}/>
            },
            {
                key: "phone",
                title: "Phone",
                render: (row) => <Highlighter text={row.phone} search={filter.phone}/>
            },
            {key: "login", title: "Login", render: (row) => <Highlighter text={row.login} search={filter.login}/>},
            {
                key: "userType",
                title: "User Type",
                render: (row) => <Highlighter text={row.userType} search={filter.userType}/>
            },
            {
                key: "enabled",
                title: "User Enabled",
                render: row => row.enabled
                        ? <Tooltip title={"User Enabled"}><CheckCircleOutlinedIcon color={"success"}/></Tooltip>
                        : <Tooltip title={"User Disabled"}><CancelOutlinedIcon color={"error"}/></Tooltip>
            },
            {
                key: "verified",
                title: "User Verified",
                render: (row) => row.verified
                        ? <Tooltip title={"User Verified"}><VerifiedUserOutlinedIcon color={"success"}/></Tooltip>
                        : <Tooltip title={"User Not Verified"}><GppBadOutlinedIcon color={"error"}/></Tooltip>
            },
            {
                key: "controlPanel",
                disableSort: true,
                title: <Stack flexDirection={"row-reverse"}><Button onClick={() => navigate("/users/new")}>New User</Button></Stack>,
                render: (row) => <Stack flexDirection={"row"} gap="5px" justifyContent={"space-evenly"}>
                    <IconButton onClick={() => {
                        navigate("/users/" + row.id);
                    }}>
                        <EditNoteOutlinedIcon/>
                    </IconButton>

                    <Delete onConfirm={() => {
                        deleteMutation.mutate(row.id);
                    }}/>
                </Stack>
            }
        ]
    }, [deleteMutation, filter.userType, filter.login, filter.username, navigate, filter.phone]);

    function userFilter(filter: UsersFilter) {
        const out = {...filter}
        if (out.login) {
            out.login = '%' + out.login + '%'
        }
        if (out.username) {
            out.username = '%' + out.username + '%'
        }
        if (out.phone) {
            out.phone = '%' + out.phone + '%'
        }
        return sanitizeFilterObject(out);
    }


    if (idToEdit) {
        return <UserEditForm id={idToEdit} parent={"/users"} hideBack={false}/>
    }

    return <>
        {error && <Alert color={"error"}>{error?.name + ": " + error?.message}</Alert>}
        <Stack flexDirection={"column"} className={"mb-15"}>
            <Stack flexDirection={"row"} gap={'15px'}>
                <TextInput label={"Username"} value={filter.username || ''} onChange={(e) => setFilter({
                    ...filter, page: 0, username: e.target.value
                })}/>
                <TextInput label={"Phone"} value={filter.phone || ''} onChange={(e) => setFilter({
                    ...filter, page: 0, phone: e.target.value
                })}/>
                <TextInput label={"Login"} value={filter.login || ''} onChange={(e) => setFilter({
                    ...filter, page: 0, login: e.target.value
                })}/>
                {<Stack flexDirection={"row"} alignItems={"center"}>
                    <Rating
                        value={(filter.withPremium ? 1 : 0)}
                        precision={1}
                        size={'large'}
                        max={1}
                        onChange={() => {
                            setFilter({...filter, page: 0, withPremium: !filter.withPremium})
                        }}
                        emptyIcon={<StarBorderOutlinedIcon style={{ opacity: 0.55 }} fontSize="inherit"/>}
                    />
                    <label className={"pointer"} onClick={e =>
                        setFilter({...filter, page: 0, withPremium: !filter.withPremium})}>
                        Premium Users
                    </label>
                </Stack>}
            </Stack>
        </Stack>
        <Table data={data?.content || []}
               sort={{
                   key: filter.sortKey,
                   order: filter.sortOrder,
                   onChange: (key, order) => setFilter({...filter, page: 0, sortKey: key, sortOrder: order})
               }}
               isLoading={isFetching}
               total={data?.totalElements || 0}
               page={filter.page}
               size={filter.size}
               headers={headers}
               pageChange={(page, size) => setFilter({...filter, page, size})}/>
    </>
};
